import { useEffect, useRef, useState } from "react";
import { IoCheckmarkCircle, IoWarning, IoCloseCircle, IoClose } from "react-icons/io5";

const DURATION_SUCCESS = 5500;
const DURATION_ERROR = 7000;
const DURATION_WARNING = 5500;

const VARIANTS = {
  success: {
    bg: "bg-white dark:bg-slate-900",
    border: "border-emerald-200 dark:border-emerald-800",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/50",
    icon: IoCheckmarkCircle,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    title: "Başarılı",
    titleColor: "text-emerald-800 dark:text-emerald-200",
    textColor: "text-slate-700 dark:text-slate-300",
    progressBg: "bg-emerald-500",
  },
  error: {
    bg: "bg-white dark:bg-slate-900",
    border: "border-red-200 dark:border-red-800",
    iconBg: "bg-red-100 dark:bg-red-900/50",
    icon: IoCloseCircle,
    iconColor: "text-red-600 dark:text-red-400",
    title: "Hata",
    titleColor: "text-red-800 dark:text-red-200",
    textColor: "text-slate-700 dark:text-slate-300",
    progressBg: "bg-red-500",
  },
  warning: {
    bg: "bg-white dark:bg-slate-900",
    border: "border-amber-200 dark:border-amber-800",
    iconBg: "bg-amber-100 dark:bg-amber-900/50",
    icon: IoWarning,
    iconColor: "text-amber-600 dark:text-amber-400",
    title: "Uyarı",
    titleColor: "text-amber-800 dark:text-amber-200",
    textColor: "text-slate-700 dark:text-slate-300",
    progressBg: "bg-amber-500",
  },
};

function getVariantFromChoice(alertChoice) {
  if (alertChoice === 1) return "success";
  if (alertChoice === 2) return "error";
  if (alertChoice === 3) return "warning";
  return "success";
}

function getDurationFromVariant(variant) {
  if (variant === "error") return DURATION_ERROR;
  if (variant === "warning") return DURATION_WARNING;
  return DURATION_SUCCESS;
}

/**
 * Modern, ortak bildirim bileşeni.
 * - AlertChoice (1=başarı, 2=hata, 3=uyarı) + message: eski kullanım.
 * - onClose verilirse süre bitiminde otomatik kapanır (okunabilirlik için uzun süre).
 */
function Alert({ AlertChoice, message, onClose, variant: variantProp, duration: durationProp }) {
  const variant = variantProp || getVariantFromChoice(AlertChoice);
  const duration = durationProp ?? getDurationFromVariant(variant);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const animRef = useRef(null);

  const config = VARIANTS[variant] || VARIANTS.success;
  const Icon = config.icon;

  useEffect(() => {
    if (!onClose || duration <= 0) return;
    startTimeRef.current = Date.now();
    setProgress(100);

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining > 0) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);

    const t = setTimeout(() => {
      onClose();
    }, duration);
    return () => {
      clearTimeout(t);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [onClose, duration]);

  if (!message && !AlertChoice) return null;

  return (
    <div
      role="alert"
      className={`
        relative flex items-start gap-4 p-5 rounded-2xl min-w-[320px] max-w-md
        ${config.bg} border ${config.border}
        shadow-xl shadow-slate-200/50 dark:shadow-none
        animate-toast-in pointer-events-auto
      `}
    >
      <div className={`flex-shrink-0 p-2.5 rounded-xl ${config.iconBg}`}>
        <Icon className={`w-7 h-7 ${config.iconColor}`} aria-hidden />
      </div>

      <div className="flex-1 min-w-0 pr-8">
        <h3 className={`text-base font-semibold ${config.titleColor} mb-1`}>
          {config.title}
        </h3>
        <p className={`text-[15px] leading-relaxed ${config.textColor}`}>
          {message}
        </p>
        {onClose && duration > 0 && (
          <div className="mt-3 h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full rounded-full ${config.progressBg} transition-all duration-150`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Kapat"
        >
          <IoClose className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

export default Alert;
/** Eski kullanım için: <Alerts AlertChoice={1|2|3} message="..." onClose={() => setAlert(0)} /> — onClose verilirse uzun süre + ilerleme çubuğu */
export { Alert as Alerts };
